import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { $api } from '../../api';
import ErrorMessage from '../ErrorMessage';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityName: string;
  onConfirm: () => void;
  organizationId: string;
  entityId: string;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ 
  isOpen, 
  onClose, 
  entityName, 
  onConfirm,
  organizationId,
  entityId
}) => {
  const [confirmText, setConfirmText] = useState('');
  
  // Delete entity mutation
  const deleteEntityMutation = $api.useMutation('delete', '/api/organization/{organization_id}/entities/{id}');
  
  const handleConfirm = async () => {
    if (confirmText.toLowerCase() !== 'confirmer') {
      return;
    }

    deleteEntityMutation.mutate(
      {
        params: {
          path: {
            organization_id: organizationId,
            id: entityId,
          },
        },
      },
      {
        onSuccess: () => {
          onConfirm();
          setConfirmText('');
          onClose();
        },
      }
    );
  };

  const handleClose = () => {
    setConfirmText('');
    deleteEntityMutation.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <h2 className="text-xl font-semibold text-gray-900">Confirmer la suppression</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Error Message */}
          {deleteEntityMutation.error && (
            <ErrorMessage error={deleteEntityMutation.error} className="mb-4" />
          )}

          <div className="mb-4">
            <p className="text-gray-700 mb-2">
              Vous êtes sur le point de supprimer définitivement l'entité :
            </p>
            <p className="font-semibold text-gray-900 bg-gray-50 p-3 rounded-lg break-words">
              {entityName}
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="text-red-800 font-medium">Attention !</h4>
                <p className="text-red-700 text-sm mt-1">
                  Cette action est irréversible. Toutes les données associées à cette entité 
                  (documents, vérifications KYC, historique) seront définitivement perdues.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pour confirmer, tapez <span className="font-semibold">"confirmer"</span> ci-dessous :
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="confirmer"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              disabled={deleteEntityMutation.isPending}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={confirmText.toLowerCase() !== 'confirmer' || deleteEntityMutation.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {deleteEntityMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Suppression...</span>
                </>
              ) : (
                <span>Supprimer définitivement</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;